import isNumber from 'lodash/isNumber';
import memoize from 'lodash/memoize';
import reduce from 'lodash/reduce';
import { buildEmptyItemsArray } from '@yme/atoms/pagination';
import { CATEGORIES_ID, DRIVER_LICENCE_MAP, FilterType, NA_VALUE, ProductUnitType, QueryParams, UNITS_WITHOUT_LICENCE, VARIANTS_ACCESSORY_TYPE, VARIANTS_ATTR_COLOUR_NAME, VARIANTS_ATTR_SIZE, VARIANTS_ATTR_USAGE, VARIANTS_BOATS_BRAND, VARIANTS_BOATS_HORSE_POWER_MAX, VARIANTS_BOATS_HORSE_POWER_MIN, VARIANTS_BOATS_LENGTH, VARIANTS_BOATS_TYPE, VARIANTS_COLLECTION, VARIANTS_DRIVER_LICENSE_CATEGORY, VARIANTS_EBIKE_RIDING_TYPE, VARIANTS_EBIKE_WHEEL_SIZE, VARIANTS_GENDER, VARIANTS_MOTORCYCLES_LIMITED_POWER_VERSION, VARIANTS_MOTORCYCLES_POWER, VARIANTS_OUTBOARD_ENGINE_CONFIGURATION, VARIANTS_OUTBOARD_ENGINE_STEERING, VARIANTS_OUTBOARD_ENGINE_WEIGHT_MAX, VARIANTS_OUTBOARD_ENGINE_WEIGHT_MIN, VARIANTS_SCOOTERS_ENGINE_SIZE, VARIANTS_SCOOTERS_POWER, VARIANTS_SCOOTERS_STORAGE, VARIANTS_SNOWMOBILES_CYLINDERS, VARIANTS_SNOWMOBILES_DISPLACEMENT, VARIANTS_SNOWMOBILES_TYPE, VARIANTS_WAVE_RUNNERS_DRY_WEIGHT, VARIANTS_WAVE_RUNNERS_ENGINE_SIZE, VARIANTS_WAVE_RUNNERS_SUPER_CHARGED, } from '@yme/common/constants';
import { BREAKPOINT } from '@yme/tokens/screen';
import { FacetFields } from '@yme/utils/bloomreach';
import { getFilterType } from '../ComparisonFilter/helpers';
import { DEFAULT_PER_PAGE_OPTIONS, DEFAULT_START_PAGE, LocalizedLabels, MeasurementUnits, PerPageLimit, RANGE_FACET_ID, RANGE_FACET_SEPARATOR, SCOOTER_ONE_HELMET_NUMBER, SCOOTER_TWO_HELMET_NUMBER, } from './constants';
export function buildPaginationData(totalItems, amountPerPage) {
    const countItemsMaximal = Math.ceil(totalItems / amountPerPage);
    return {
        currentPage: DEFAULT_START_PAGE,
        perPageOptions: DEFAULT_PER_PAGE_OPTIONS,
        countItems: countItemsMaximal,
        items: buildEmptyItemsArray(countItemsMaximal),
    };
}
export function buildCounterParameters(paginationActivePage, amountPerPage) {
    const fromValue = (paginationActivePage - 1) * amountPerPage + 1, tillValue = fromValue + amountPerPage - 1;
    return {
        fromValue,
        tillValue,
    };
}
export const getFacetsLabel = ({ fullBackValue, key, dictionary, }) => {
    const results = {
        [CATEGORIES_ID]: dictionary.productsGrid_subcategoriesFilter_label,
        [VARIANTS_COLLECTION]: dictionary.productsGrid_collectionFilter_label,
        [VARIANTS_ACCESSORY_TYPE]: dictionary.productsGrid_typeFilter_label,
        [VARIANTS_GENDER]: dictionary.productsGrid_genderFilter_label,
        [VARIANTS_ATTR_SIZE]: dictionary.productsGrid_sizeFilter_label,
        [VARIANTS_ATTR_COLOUR_NAME]: dictionary.productsGrid_colourFilter_label,
        [VARIANTS_ATTR_USAGE]: dictionary.productsGrid_usageFilter_label,
        [FacetFields.SUBCATEGORIES]: dictionary.productsGrid_subcategoriesFilter_label,
        [FacetFields.COLLECTION]: dictionary.productsGrid_collectionFilter_label,
        [FacetFields.TYPE]: dictionary.productsGrid_typeFilter_label,
        [FacetFields.GENDER]: dictionary.productsGrid_genderFilter_label,
        [FacetFields.SIZES]: dictionary.productsGrid_sizeFilter_label,
        [FacetFields.COLORS]: dictionary.productsGrid_colourFilter_label,
        [FacetFields.ACCESSORY_USAGE]: dictionary.productsGrid_usageFilter_label, // 'Usage'
    };
    return results[key] || fullBackValue || NA_VALUE;
};
function getClearedFacetsValues(values, id) {
    return values?.length
        ? values.reduce((acc, item) => {
            const { name = '', value, valueEn, label, } = id && id === CATEGORIES_ID
                ? {
                    name: item?.name,
                    value: item?.value,
                    valueEn: item?.valueEn,
                    label: item?.name || item.value,
                }
                : item;
            const itemProps = {
                ...item,
                name,
                value,
                valueEn,
                label,
                checked: false,
            };
            if (!acc && itemProps) {
                return [itemProps];
            }
            return itemProps ? [...acc, itemProps] : [...acc];
        }, [])
        : [];
}
function findFacetById(list, id) {
    return list.find((item) => item?.id === id);
}
function facetResult(facets, facetId, dictionary = {}) {
    const facet = findFacetById(facets, facetId);
    return {
        values: getClearedFacetsValues(facet?.values, facetId),
        name: getFacetsLabel({
            dictionary,
            key: facetId,
            fullBackValue: facet?.name,
        }),
        total: facet?.total || 0,
        id: facetId,
    };
}
export function getFacetsData({ facetsData = [], facetsConfig = [], dictionary, }) {
    if (facetsData?.length) {
        return facetsConfig.map((facetId) => {
            return facetResult(facetsData, facetId, dictionary);
        });
    }
    return [];
}
function getCategoryIds(categoryIds) {
    const finalCategoryIds = categoryIds.map((item) => item?.id);
    return finalCategoryIds.join(',');
}
export function buildRequestParam({ categoryIds, extraQueryParam = [], facetsConfig, selectedFacets = '', sortOrder, amountPerPage, currentPage, search = '', }) {
    const categoryIdsList = categoryIds && getCategoryIds(categoryIds);
    if (!categoryIdsList) {
        return undefined;
    }
    return {
        query: [`categories.id:subtree("${categoryIdsList}")`, ...extraQueryParam].join('|'),
        facets: facetsConfig.join('|'),
        selectedFacets,
        sortOrder,
        limit: amountPerPage,
        offset: isNumber(currentPage) ? amountPerPage * (currentPage - 1) : 0,
        search: search.trim(),
    };
}
export function getDefaultPerPageLimit(width, perPageLimitSetting) {
    if (width >= BREAKPOINT.lg) {
        return perPageLimitSetting || PerPageLimit.DESKTOP_COMMON_DEFAULT;
    }
    if (width >= BREAKPOINT.md && width < BREAKPOINT.lg) {
        return PerPageLimit.TABLET_COMMON_DEFAULT;
    }
    return PerPageLimit.MOBILE_COMMON_DEFAULT;
}
const facetsParamsCacheResolver = (facetsId, params) => reduce(facetsId, (acc, curr) => (acc += params.get(curr)), '');
const getFacetsParams = (facetsId, params) => {
    const facetsParams = {};
    facetsId.forEach((facet) => {
        facetsParams[facet] = params.get(facet);
    });
    return facetsParams;
};
const getMemoizedFacetsParams = memoize(getFacetsParams, facetsParamsCacheResolver);
export function getParamsFromURL(search, facetsId) {
    const params = new URLSearchParams(search);
    const facetsParams = getMemoizedFacetsParams(facetsId, params);
    return {
        pageParam: params.get(QueryParams.PAGE),
        perPageParam: params.get(QueryParams.PER_PAGE),
        sortOrderParam: params.get(QueryParams.SORT),
        searchParam: params.get(QueryParams.SEARCH),
        discountedParam: params.get(QueryParams.DISCOUNTED),
        product: params.get(QueryParams.PRODUCT),
        isDiscountedParam: params.get(FacetFields.IS_DISCOUNTED),
        typeEnParam: params.get(FacetFields.TYPE_EN),
        facetsParams,
    };
}
function getLocalizedLabel(label, t) {
    switch (label) {
        case LocalizedLabels.YES:
            return t('common_yes_label');
        case LocalizedLabels.NO:
            return t('common_no_label');
        default:
            return label;
    }
}
const sortScooterStorageFilter = (a, b) => {
    const aValue = a.value || '';
    const bValue = b.value || '';
    const aHasOneHelmet = aValue.includes(SCOOTER_ONE_HELMET_NUMBER);
    const bHasOneHelmet = bValue.includes(SCOOTER_ONE_HELMET_NUMBER);
    const aHasTwoHelmets = aValue.includes(SCOOTER_TWO_HELMET_NUMBER);
    const bHasTwoHelmets = bValue.includes(SCOOTER_TWO_HELMET_NUMBER);
    if (aHasOneHelmet) {
        return bHasOneHelmet ? aValue.localeCompare(bValue) : -1;
    }
    else if (aHasTwoHelmets) {
        return bHasTwoHelmets ? aValue.localeCompare(bValue) : 1;
    }
    else {
        return bHasOneHelmet || bHasTwoHelmets ? -1 : aValue.localeCompare(bValue);
    }
};
const sortDriversLicences = (a, b) => DRIVER_LICENCE_MAP.ALL.indexOf(a.value) - DRIVER_LICENCE_MAP.ALL.indexOf(b.value);
const checkIsScooterLockStorage = (storageType) => !storageType.includes(SCOOTER_ONE_HELMET_NUMBER) &&
    !storageType.includes(SCOOTER_TWO_HELMET_NUMBER);
export function getOptionsForFilter({ values, t, facetId, additionalText = undefined, }) {
    const options = values
        .filter(({ value }) => {
        return value !== '';
    })
        .map(({ value = '', name = '', checked, count, valueEn }) => ({
        label: name || getLocalizedLabel(value, t),
        value,
        valueEn: valueEn || value,
        checked,
        count,
    }));
    if (facetId === VARIANTS_DRIVER_LICENSE_CATEGORY) {
        return options
            .filter((option) => DRIVER_LICENCE_MAP.ALL.includes(option.label) && option.label !== UNITS_WITHOUT_LICENCE)
            .sort(sortDriversLicences);
    }
    if (facetId === VARIANTS_SCOOTERS_STORAGE) {
        return options
            .map((option) => ({
            ...option,
            additionalText: !checkIsScooterLockStorage(option.value) ? additionalText : undefined,
        }))
            .sort(sortScooterStorageFilter);
    }
    return options;
}
export function getFacetsValues(facets, facetsParams, filterFacetsConfig) {
    return facets.map((facet) => {
        const currentFacetParams = facetsParams[facet.id];
        if (filterFacetsConfig &&
            getFilterType(filterFacetsConfig, facet.id) === FilterType.TOGGLE &&
            !facet.values.find((item) => item.value === LocalizedLabels.YES)) {
            facet.values.push({
                checked: false,
                valueEn: LocalizedLabels.YES,
                value: LocalizedLabels.YES,
                count: 0,
                idByValue: '',
            });
        }
        if (currentFacetParams) {
            const isRangeFacet = currentFacetParams.includes(RANGE_FACET_SEPARATOR);
            const splitCurrentFacetParams = currentFacetParams.split(isRangeFacet ? RANGE_FACET_SEPARATOR : ',');
            return {
                ...facet,
                values: facet.values.map((value) => ({
                    ...value,
                    checked: !isRangeFacet
                        ? splitCurrentFacetParams.includes(value.value) ||
                            splitCurrentFacetParams.includes(value.valueEn)
                        : value.checked,
                    rangeLow: isRangeFacet ? Number(splitCurrentFacetParams[0]) : undefined,
                    rangeHigh: isRangeFacet ? Number(splitCurrentFacetParams[1]) : undefined,
                })),
            };
        }
        return {
            ...facet,
            values: facet.values.map((value) => ({
                ...value,
                checked: false,
                rangeLow: undefined,
                rangeHigh: undefined,
            })),
        };
    });
}
export function mergeFacetsCounts(prevFacets, currentFacets) {
    return prevFacets.map((facet) => {
        const currentFacet = currentFacets.find((item) => item.id === facet.id);
        return {
            ...facet,
            values: facet.values.map((value) => {
                const currentValue = currentFacet?.values.find((item) => {
                    return item.value === value.value;
                });
                return {
                    ...value,
                    count: currentValue?.count ?? 0,
                };
            }),
        };
    });
}
export function mergeProductFilterFacetsCounts(prevFacets, currentFacets) {
    const result = prevFacets.map((facet) => {
        const currentFacet = currentFacets.find((item) => item.id === facet.id);
        return {
            ...facet,
            values: facet.values.map((value) => {
                const currentValue = currentFacet &&
                    currentFacet?.values.find((item) => {
                        return (item?.name && item.name === value.value) || item.value === value.value;
                    });
                return {
                    ...value,
                    count: currentValue?.count ?? 0,
                };
            }),
        };
    });
    return result;
}
export const parseDriverLicenceFilter = (key) => {
    if (typeof key === 'undefined' || !(key in DRIVER_LICENCE_MAP)) {
        return [];
    }
    return DRIVER_LICENCE_MAP[key].map((licence) => `"${licence}"`);
};
export function getSelectedFacetsFromUrl(facetsParams) {
    const selectedFacets = Object.entries(facetsParams)
        .filter((facetParam) => facetParam[1] !== null)
        .map(([id, value]) => {
        const isRangeFacet = value?.includes(RANGE_FACET_SEPARATOR);
        if (isRangeFacet) {
            return `${id}:range (${value})`;
        }
        if (id === VARIANTS_DRIVER_LICENSE_CATEGORY) {
            const facetValues = value?.split(',');
            const currentFacetValues = facetValues?.map((facet) => parseDriverLicenceFilter(facet));
            return `${id}: ${Array.from(new Set(currentFacetValues?.flat())).join(',')}`;
        }
        return `${id}: ${value
            ?.split(',')
            .map((param) => JSON.stringify(param))
            .join(',')}`;
    });
    return selectedFacets.join('|');
}
export function getUnitFacetsConfig(productUnitType, options = {
    is35kwFilterDisabled: false,
}) {
    switch (productUnitType) {
        case ProductUnitType.EBIKE:
            return [
                {
                    id: VARIANTS_EBIKE_RIDING_TYPE,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_EBIKE_WHEEL_SIZE,
                    type: FilterType.CHECKLIST,
                },
            ];
        case ProductUnitType.MOTORCYCLES:
            return [
                {
                    id: VARIANTS_DRIVER_LICENSE_CATEGORY,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_MOTORCYCLES_POWER,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.KW,
                },
                options.is35kwFilterDisabled
                    ? null
                    : {
                        id: VARIANTS_MOTORCYCLES_LIMITED_POWER_VERSION,
                        type: FilterType.TOGGLE,
                    },
            ].filter(Boolean);
        case ProductUnitType.SCOOTERS:
            return [
                {
                    id: VARIANTS_SCOOTERS_STORAGE,
                    type: FilterType.RADIO,
                },
                {
                    id: VARIANTS_SCOOTERS_POWER,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.KW,
                },
                {
                    id: VARIANTS_SCOOTERS_ENGINE_SIZE,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.CC,
                },
                {
                    id: VARIANTS_DRIVER_LICENSE_CATEGORY,
                    type: FilterType.CHECKLIST,
                },
            ];
        case ProductUnitType.WAVE_RUNNERS:
            return [
                {
                    id: VARIANTS_WAVE_RUNNERS_ENGINE_SIZE,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.CC,
                },
                {
                    id: VARIANTS_WAVE_RUNNERS_SUPER_CHARGED,
                    type: FilterType.TOGGLE,
                },
                {
                    id: VARIANTS_WAVE_RUNNERS_DRY_WEIGHT,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.KG,
                },
            ];
        case ProductUnitType.BOAT:
            return [
                {
                    id: VARIANTS_BOATS_LENGTH,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.M,
                },
                {
                    id: VARIANTS_BOATS_TYPE,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_BOATS_BRAND,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_BOATS_HORSE_POWER_MIN,
                    type: FilterType.RANGE_MIN,
                    unit: MeasurementUnits.HP,
                },
                {
                    id: VARIANTS_BOATS_HORSE_POWER_MAX,
                    type: FilterType.RANGE_MAX,
                    unit: MeasurementUnits.HP,
                },
            ];
        case ProductUnitType.OUTBOARD_ENGINES:
            return [
                {
                    id: VARIANTS_OUTBOARD_ENGINE_CONFIGURATION,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_OUTBOARD_ENGINE_STEERING,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_OUTBOARD_ENGINE_WEIGHT_MIN,
                    type: FilterType.RANGE_MIN,
                    unit: MeasurementUnits.KG,
                },
                {
                    id: VARIANTS_OUTBOARD_ENGINE_WEIGHT_MAX,
                    type: FilterType.RANGE_MAX,
                    unit: MeasurementUnits.KG,
                },
            ];
        case ProductUnitType.SNOWMOBILES:
            return [
                {
                    id: VARIANTS_SNOWMOBILES_TYPE,
                    type: FilterType.CHECKLIST,
                },
                {
                    id: VARIANTS_SNOWMOBILES_DISPLACEMENT,
                    type: FilterType.RANGE,
                    unit: MeasurementUnits.CC,
                },
                {
                    id: VARIANTS_SNOWMOBILES_CYLINDERS,
                    type: FilterType.CHECKLIST,
                },
            ];
        case ProductUnitType.OTHER:
        default:
            return [];
    }
}
export function getInitialFacetsConfig(filterFacetsConfig) {
    return filterFacetsConfig.map((filterFacet) => {
        const rangeTypes = [FilterType.RANGE, FilterType.RANGE_MIN, FilterType.RANGE_MAX];
        if (rangeTypes.includes(filterFacet.type)) {
            return `${filterFacet.id}:range (0 to *)`;
        }
        return filterFacet.id;
    });
}
function getAppliedValue(value) {
    return value.checked || (isNumber(value.rangeLow) && isNumber(value.rangeHigh));
}
export function getCountAppliedValues(values) {
    return values.filter(getAppliedValue).length;
}
export function getCountAppliedFilters(facets) {
    const checkedFacetsLength = facets.filter((facet) => !facet.id.includes(RANGE_FACET_ID) && facet.values.some(getAppliedValue)).length;
    const rangeFacets = facets.filter((facet) => facet.id.includes(RANGE_FACET_ID));
    const rangeFacetsLength = rangeFacets.length
        ? Number(rangeFacets.every((facet) => facet.values.some((item) => isNumber(item.rangeHigh) && isNumber(item.rangeLow))))
        : 0;
    return rangeFacetsLength + checkedFacetsLength;
}
